import React, { useState } from 'react';
import { Lock, LockOpen, Info } from 'lucide-react';
import { PuertaSegundoFactor, useVentanaSegundoFactor } from '../../componentes/ui-v2.5';
import { fetchProrrogarVentana } from '../../../peticiones_API/PeticionesSeguridad';
import { guardarVentana } from '../../../funciones/ventanaSegundoFactor';

/**
 * Fase A1 · el candado de la barra superior.
 *
 * ── POR QUÉ AQUÍ Y NO EN CADA PANTALLA ────────────────────────────────────────────────────
 *
 * El contador vivía dentro del listado y de la ficha, ocupando sitio del contenido y, en la
 * ficha, encima de las pestañas, donde no pega.
 *
 * Y estaba en el sitio equivocado por la misma razón que lo estaba la ventana: **la ventana es
 * de quien mira, no de la pantalla**. Su indicador vive donde vive la identidad — junto al
 * nombre y las notificaciones—, no donde se usa.
 *
 * Cerrado cuando no hay ventana; abierto con la cuenta atrás al lado cuando la hay. Cerrado, al
 * pulsarlo se abre la puerta, y quien no tenga el segundo factor recibe la invitación que ya
 * existe. Abierto, al pulsarlo sale el panel: cuánto queda, el máximo en hora y cerrarla en este
 * navegador (15/10).
 *
 * Lo que NO sube aquí, porque pasa donde pasa: el aviso de que las contraseñas están visibles y
 * de que la consulta queda registrada —eso es del listado, junto a las columnas— y el que al
 * agotarse desaparezcan y Soporte vuelva a Personal.
 */

const reloj = (s) => {
  const t = Math.max(0, Math.floor(s));
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
};

/* «+N MIN» (15/10; para todos desde el 15/10). Alarga lo que dura la ventana de quien mira: +10
 * quien la tiene de 10, +5 quien la tiene de 5. Lo decide el servidor por el rol; la pantalla
 * sólo pone en el botón los minutos que ya trae la ventana. Se apaga al llegar al tope: 60
 * minutos desde que se metió el código. Y si el servidor dice que no, se enseña su motivo. */
const MARGEN_TOPE_MS = 30 * 1000;

/** La hora de un instante, «13:42». El máximo en HORA dice más que en minutos: el tope es lo que
 *  la gente no sabe que existe, y «hasta las 13:42» lo enseña sin explicarlo. */
const hora = (ms) => (ms ? new Date(ms).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '');

export default function CandadoVentana() {
  const v = useVentanaSegundoFactor();
  const [puerta, setPuerta] = useState(false);
  const [info, setInfo] = useState(false);
  /* EL PANEL DE LA VENTANA ABIERTA (15/10). Antes, pulsar el candado abierto volvía a pedir el
   * código, y eso no tenía sentido: ya estaba abierta. Ahora, abierta, el candado enseña cuánto
   * queda y hasta cuándo, y deja cerrarla; la puerta sólo sale con el candado cerrado. Empezar de
   * cero son dos pasos explícitos: cerrar y volver a verificar. */
  const [panel, setPanel] = useState(false);
  const [alargando, setAlargando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const poco = v.activa && v.restante <= 60;
  const enTope = v.tope != null && v.expira != null && v.expira >= v.tope - MARGEN_TOPE_MS;

  const alargar = async () => {
    if (alargando || !v.ventana) return;
    setAlargando(true); setAviso(null);
    try {
      const r = await fetchProrrogarVentana(v.ventana);
      // Los minutos de la ventana no cambian: se conserva lo que había. Lo demás, del servidor.
      guardarVentana(r.ventana, r.expiraEn, v.minutos, { prorrogable: r.prorrogable, tope: r.tope });
      if (r.recortada) setAviso('Llegaste al máximo de 60 minutos desde el código.');
    } catch (e) {
      setAviso(e?.response?.data?.message || 'No se ha podido alargar la verificación.');
    } finally {
      setAlargando(false);
    }
  };

  return (
    <>
      <div className="relative flex items-center">
        <button
          type="button"
          onClick={() => (v.activa ? setPanel((x) => !x) : setPuerta(true))}
          aria-expanded={v.activa ? panel : undefined}
          title={v.activa
            ? `Verificación activa · quedan ${reloj(v.restante)}`
            : 'Verificar en dos pasos para ver datos sensibles'}
          aria-label={v.activa ? 'Verificación activa' : 'Verificar en dos pasos'}
          className={`inline-flex items-center gap-1.5 rounded-v25-md px-2 py-1.5 text-[12px] transition-colors
            ${v.activa
              ? (poco ? 'bg-v25-waiting-bg text-v25-waiting-fg' : 'bg-v25-active-bg text-v25-active-fg')
              : 'text-v25-ink-500 hover:bg-v25-surface-sunken'}`}
        >
          {v.activa ? <LockOpen size={16} /> : <Lock size={16} />}
          {/* El contador SOLO cuando hay ventana: sin ella, el candado no promete nada. */}
          {v.activa && <span className="font-mono tabular-nums">{reloj(v.restante)}</span>}
        </button>

        {v.activa && v.prorrogable && (
          <button
            type="button"
            onClick={alargar}
            disabled={alargando || enTope}
            title={enTope
              ? 'Máximo alcanzado: 60 minutos desde el código. Al agotarse, vuelve a introducirlo.'
              : `Alargar la verificación ${v.minutos || ''} minutos más`}
            aria-label={`Alargar la verificación ${v.minutos || ""} minutos`}
            className="ml-1 rounded-v25-md border border-v25-line px-1.5 py-1 text-[11px] font-medium text-v25-ink-600 hover:bg-v25-surface-sunken disabled:opacity-40"
          >
            {alargando ? '…' : `+${v.minutos || ''} min`}
          </button>
        )}

        <button
          type="button"
          onClick={() => setInfo((x) => !x)}
          title="Qué es esto"
          aria-label="Qué es esto"
          className="ml-0.5 rounded-v25-full p-1 text-v25-ink-400 hover:bg-v25-surface-sunken hover:text-v25-ink-700"
        >
          <Info size={14} />
        </button>

        {panel && v.activa && (
          <div role="dialog" aria-label="Tu verificación"
            className="absolute right-0 top-full z-50 mt-2 w-[300px] rounded-v25-lg border border-v25-line bg-white p-3 text-[12px] shadow-v25-modal">
            <p className="font-medium text-v25-ink-900">Verificación activa · quedan {reloj(v.restante)}</p>
            <p className="mt-1 text-v25-ink-600">Caduca a las {hora(v.expira)}.</p>
            {v.tope && (
              <p className="mt-0.5 text-v25-ink-500">
                Como mucho hasta las {hora(v.tope)}: una hora desde que metiste el código. Después hay
                que volver a introducirlo.
              </p>
            )}
            <div className="mt-3 border-t border-v25-line pt-2.5">
              {/* EL NOMBRE DICE LO QUE HACE. Cerrar es de este navegador: no anula la verificación
                  en el servidor (decisión de Iban, 15/10: comodidad, no garantía). «Cerrar ahora» a
                  secas prometería más de lo que hace. */}
              <button type="button"
                onClick={() => { v.cerrar(); setPanel(false); }}
                title={`Se cierra aquí y en las demás pestañas de este navegador. No se anula en el servidor: si alguien tuviera una copia, caducaría sola a las ${hora(v.expira)}.`}
                className="rounded-v25-md border border-v25-line px-2.5 py-1.5 font-medium text-v25-ink-700 hover:bg-v25-surface-sunken">
                Cerrar en este navegador
              </button>
              <p className="mt-1.5 text-[11px] text-v25-ink-400">
                Se cierra aquí y en las demás pestañas. No se anula en el servidor: caduca sola a las {hora(v.expira)}.
              </p>
            </div>
          </div>
        )}

        {aviso && (
          <div role="status" className="absolute right-0 top-full z-50 mt-2 w-[280px] rounded-v25-lg border border-v25-line bg-white p-2.5 text-[12px] text-v25-ink-700 shadow-v25-modal">
            {aviso}
            <button type="button" onClick={() => setAviso(null)} className="ml-2 text-v25-brand-600 hover:underline">Vale</button>
          </div>
        )}

        {/* La explicación que antes ocupaba cuatro líneas fijas, ahora bajo demanda. */}
        {info && (
          <div className="absolute right-0 top-full z-50 mt-2 w-[320px] rounded-v25-lg border border-v25-line bg-white p-3 shadow-v25-modal">
            <p className="text-[12px] text-v25-ink-700">
              Para ver contraseñas y datos confidenciales hace falta verificar tu segundo factor.
              Al hacerlo se abre una ventana de unos minutos: mientras dure, puedes consultarlos
              sin volver a teclear el código, en cualquier pantalla.
            </p>
            <p className="mt-2 text-[12px] text-v25-ink-500">
              Al agotarse, esos datos se ocultan solos. Cada consulta queda registrada.
              {v.minutos ? ` Tu verificación dura ${v.minutos} minutos.` : ''}
            </p>
            <button type="button" onClick={() => setInfo(false)}
              className="mt-2 text-[12px] text-v25-brand-600 hover:underline">
              Entendido
            </button>
          </div>
        )}
      </div>

      {/* Sin `onAbierta`: la PUERTA guarda la ventana desde Fase 12 §6.7bis, y este componente
          no tiene nada que hacer después — sólo enseña el candado. El estado le llega por el
          evento que emite `guardarVentana`, así que tampoco hay orden que respetar.
          Llamar aquí a `v.abrir` la guardaría dos veces: inofensivo hoy, y la copia que un día
          se separa. */}
      <PuertaSegundoFactor
        open={puerta}
        onClose={() => setPuerta(false)}
        queVasAVer="contraseñas y datos confidenciales"
      />
    </>
  );
}
